import { useState, useEffect } from 'react';
import { getSupabaseClient } from '../lib/supabase';

// Days of the week
const WEEKDAYS = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

// Generate time options in 30-minute increments
const generateTimeOptions = () => {
  const options: { value: number; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (const minute of [0, 30]) {
      const minutes = hour * 60 + minute;
      const h = hour % 12 || 12;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${h}:${minute.toString().padStart(2, '0')} ${ampm}`;
      options.push({ value: minutes, label });
    }
  }
  // Add end of day option
  options.push({ value: 1440, label: '12:00 AM (next day)' });
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

export interface WorkingHour {
  weekday: number;
  start_minutes: number;
  end_minutes: number;
  is_active: boolean;
}

interface WorkingHoursEditorProps {
  onChange?: (hours: WorkingHour[]) => void;
  onSave?: () => void;
  showSaveButton?: boolean;
  compact?: boolean;
}

// Default 9-5 Mon-Fri hours
const getDefaultHours = (): WorkingHour[] => {
  return WEEKDAYS.map(day => ({
    weekday: day.value,
    start_minutes: 540, // 9:00 AM
    end_minutes: 1020,  // 5:00 PM
    is_active: day.value >= 1 && day.value <= 5, // Mon-Fri active
  }));
};

export default function WorkingHoursEditor({
  onChange,
  onSave,
  showSaveButton = true,
  compact = false,
}: WorkingHoursEditorProps) {
  const supabase = getSupabaseClient();
  const [hours, setHours] = useState<WorkingHour[]>(getDefaultHours());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load existing hours on mount
  useEffect(() => {
    const loadHours = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          setLoading(false);
          return;
        }

        // Call the RPC function to get hours (with defaults if none set)
        const { data, error: rpcError } = await supabase
          .rpc('get_guide_working_hours', { p_guide_id: user.id });

        if (rpcError) {
          console.error('Error loading working hours:', rpcError);
          // Use defaults on error
        } else if (data && data.length > 0) {
          setHours(data.map((h: any) => ({
            weekday: h.weekday,
            start_minutes: h.start_minutes,
            end_minutes: h.end_minutes,
            is_active: h.is_active,
          })));
        }
      } catch (err) {
        console.error('Error loading working hours:', err);
      } finally {
        setLoading(false);
      }
    };

    loadHours();
  }, []);

  // Notify parent of changes
  useEffect(() => {
    if (!loading && onChange) {
      onChange(hours);
    }
  }, [hours, loading]);

  const updateHour = (weekday: number, field: keyof WorkingHour, value: number | boolean) => {
    setHours(prev => prev.map(h => {
      if (h.weekday === weekday) {
        const updated = { ...h, [field]: value };
        // Ensure end is after start
        if (field === 'start_minutes' && typeof value === 'number' && updated.end_minutes <= value) {
          updated.end_minutes = Math.min(value + 60, 1440);
        }
        return updated;
      }
      return h;
    }));
    setSuccess(false);
  };

  const copyToWeekdays = () => {
    // Copy Monday's hours to Tue-Fri
    const monday = hours.find(h => h.weekday === 1);
    if (monday) {
      setHours(prev => prev.map(h => {
        if (h.weekday >= 2 && h.weekday <= 5) {
          return {
            ...h,
            start_minutes: monday.start_minutes,
            end_minutes: monday.end_minutes,
            is_active: monday.is_active,
          };
        }
        return h;
      }));
    }
    setSuccess(false);
  };

  const resetToDefault = () => {
    setHours(getDefaultHours());
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setError('Authentication error. Please log in again.');
        return;
      }

      // Call the RPC function to upsert hours
      const { error: rpcError } = await supabase
        .rpc('upsert_guide_working_hours', {
          p_guide_id: user.id,
          p_hours: hours,
        });

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      setSuccess(true);
      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('Error saving working hours:', err);
      setError(err instanceof Error ? err.message : 'Failed to save working hours');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#304031]"></div>
        <span className="ml-3 text-sm text-[#304031]/70">Loading working hours...</span>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'space-y-6'}>
      {/* Quick actions */}
      <div className="flex flex-wrap gap-3 mb-4">
        <button
          type="button"
          onClick={copyToWeekdays}
          className="text-sm text-[#304031] underline hover:text-[#304031]/70 transition-colors"
        >
          Copy Monday to Tue–Fri
        </button>
        <button
          type="button"
          onClick={resetToDefault}
          className="text-sm text-[#304031] underline hover:text-[#304031]/70 transition-colors"
        >
          Reset to 9–5
        </button>
      </div>

      {/* Days grid */}
      <div className="space-y-3">
        {WEEKDAYS.map(day => {
          const hour = hours.find(h => h.weekday === day.value);
          if (!hour) return null;

          return (
            <div
              key={day.value}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                hour.is_active
                  ? 'border-[#304031]/30 bg-[#fffcef]'
                  : 'border-[#a0978a]/30 bg-[#f5f3eb]'
              }`}
            >
              {/* Day toggle */}
              <label className="flex items-center gap-2 min-w-[120px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={hour.is_active}
                  onChange={(e) => updateHour(day.value, 'is_active', e.target.checked)}
                  className="h-5 w-5 rounded border-[#304031]/30 text-[#304031] focus:ring-[#304031]"
                />
                <span className={`font-medium ${hour.is_active ? 'text-[#304031]' : 'text-[#304031]/50'}`}>
                  {compact ? day.short : day.label}
                </span>
              </label>

              {/* Time selectors */}
              {hour.is_active && (
                <div className="flex items-center gap-2 flex-1">
                  <select
                    value={hour.start_minutes}
                    onChange={(e) => updateHour(day.value, 'start_minutes', parseInt(e.target.value))}
                    className="flex-1 rounded-md border border-[#304031]/30 bg-[#fffcef] px-3 py-2 text-sm text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-1 focus:ring-[#304031]"
                  >
                    {TIME_OPTIONS.filter(t => t.value < 1440).map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <span className="text-[#304031]/70">to</span>
                  <select
                    value={hour.end_minutes}
                    onChange={(e) => updateHour(day.value, 'end_minutes', parseInt(e.target.value))}
                    className="flex-1 rounded-md border border-[#304031]/30 bg-[#fffcef] px-3 py-2 text-sm text-[#304031] focus:border-[#304031] focus:outline-none focus:ring-1 focus:ring-[#304031]"
                  >
                    {TIME_OPTIONS.filter(t => t.value > hour.start_minutes).map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Closed indicator */}
              {!hour.is_active && (
                <span className="text-sm text-[#304031]/50 italic">Closed</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="rounded-lg border border-green-300 bg-green-50 p-3">
          <p className="text-sm text-green-800">Working hours saved successfully!</p>
        </div>
      )}

      {/* Save button */}
      {showSaveButton && (
        <div className="pt-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition ${
              saving
                ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
            }`}
          >
            {saving && (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#fef6d6] border-t-transparent"></div>
            )}
            {saving ? 'Saving...' : 'Save Consultation Call Hours'}
          </button>
        </div>
      )}
    </div>
  );
}
